import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchPurchaseOrdersApi,
  fetchPurchaseOrderApi,
  fetchPoStatsApi,
  fetchPoFiltersDataApi,
  approvePoApi,
  rejectPoApi,
  rejectAtrApi,
  submitPoApi,
  unsubmitPoApi,
  cancelPoApi,
  deletePoApi,
  fulfillPoApi,
  splitPoApi,
  mergePoApi,
  bulkApprovePoApi,
  bulkRejectPoApi,
  PoListParams,
} from '../../services/api';
import type { RootState } from '../../app/store';

// ==================== Types ====================

export interface PoMinimalStore {
  id: number;
  name: string;
  storenumber?: string;
}

export interface PoMinimalUser {
  id: number;
  name: string;
  email?: string;
}

export interface PoStatus {
  id: number;
  name: string;
}

export interface PoFulfillment {
  id: number;
  name: string;
}

export interface PoApprover {
  id: number;
  user_id: number;
  user: PoMinimalUser;
  status_id: number;
  comment?: string;
  tba?: boolean;
}

export interface PoItem {
  id: number;
  description: string;
  quantity: number;
  price: number;
  unit_of_measure?: string;
  non_vat?: boolean;
  vat?: string;
}

export interface PoQuotation {
  id: number;
  quotation_number?: string;
  amount: number;
  vat?: boolean | number;
  attachment?: string;
  quotation_date?: string;
}

export interface PoInvoice {
  id: number;
  invoice_number?: string;
  amount: number;
  date?: string;
  attachment?: string;
}

export interface PoHistoryEntry {
  id: number;
  user_id: number;
  user?: PoMinimalUser;
  action: string;
  comment?: string;
  created_at: string;
}

export interface PoGrn {
  id: number;
  grn_number?: string;
  status?: string;
  created_at?: string;
}

export interface PoJobcard {
  id: number;
  job_number?: string;
  attachment?: string;
  created_at?: string;
}

export interface PoListItem {
  id: number;
  order_number?: string;
  reference_number?: string;
  date: string;
  status_id: number;
  submitted: boolean | number;
  internal?: number;
  is_repair?: number;
  is_order?: boolean;
  is_linked?: boolean;
  parent_id?: number | null;
  non_vat?: boolean;
  required_jobcard?: number;
  stores: PoMinimalStore[];
  user: PoMinimalUser;
  status?: PoStatus;
  fulfillment?: PoFulfillment;
  supplier?: { id: number; name: string };
  technician?: PoMinimalUser;
  requisition?: { id: number } | null;
  quotation?: PoQuotation;
  approvers?: PoApprover[];
  items?: PoItem[];
  grns?: PoGrn[];
  category?: { id: number; name: string };
  subCategory?: { id: number; name: string };
  updated_at: string;
  created_at?: string;
  deliverynotes_count?: number;
  jobcards_count?: number;
  invoices_count?: number;
  creditnotes_count?: number;
  tickets_count?: number;
  others_count?: number;
  is_owner?: boolean;
  can_edit?: boolean;
  can_delete?: boolean;
  can_select?: boolean;
  grn_status?: 'not_received' | 'received_fully' | 'partially_received' | null;
  is_amended?: boolean;
  documents_required?: string | null;
  non_vat_display?: boolean;
}

export interface PoDetail extends PoListItem {
  requested_by?: number;
  requestedBy?: PoMinimalUser;
  histories?: PoHistoryEntry[];
  invoices?: PoInvoice[];
  deliverynotes?: any[];
  creditnotes?: any[];
  jobcards?: PoJobcard[];
  tickets?: any[];
  category?: { id: number; name: string };
  subCategory?: { id: number; name: string };
  department?: { id: number; name: string };
  amendments?: any[];
  parent?: PoListItem | null;
  children?: PoListItem[];
  documentDeliveries?: any[];
  missingDocuments?: any[];
  equipmentRequests?: any[];
  assets?: any[];
  repairs?: any[];
  // Derived attributes
  original_total_amount?: number;
  current_total_amount?: number;
  total_with_amendments?: number;
  can_be_amended?: boolean;
  is_linked_order?: boolean;
  linked_family?: PoListItem[];
  equipment_items?: any[];
  asset_description_pairs?: any[];
  can_approve?: boolean;
  can_reject?: boolean;
  can_submit?: boolean;
  can_unsubmit?: boolean;
  can_cancel?: boolean;
}

export interface PoStatusCounts {
  pending: number;
  submitted_pending: number;
  tba: number;
  approved: number;
  rejected: number;
  cancelled: number;
}

export interface PoFiltersData {
  statuses: PoStatus[];
  stores: PoMinimalStore[];
  suppliers: { id: number; name: string }[];
  technicians: PoMinimalUser[];
  fulfillments: PoFulfillment[];
}

// ==================== State ====================

interface PoState {
  list: PoListItem[];
  current: PoDetail | null;
  statusCounts: PoStatusCounts | null;
  filtersData: PoFiltersData | null;
  loading: boolean;
  detailLoading: boolean;
  actionLoading: boolean;
  error: string | null;
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  filters: PoListParams;
  selectedIds: number[];
}

const initialState: PoState = {
  list: [],
  current: null,
  statusCounts: null,
  filtersData: null,
  loading: false,
  detailLoading: false,
  actionLoading: false,
  error: null,
  currentPage: 1,
  lastPage: 1,
  total: 0,
  perPage: 10,
  filters: {
    per_page: 10,
    page: 1,
  },
  selectedIds: [],
};

// ==================== Thunks ====================

export const fetchPurchaseOrders = createAsyncThunk(
  'po/fetchList',
  async (params: PoListParams | undefined, { rejectWithValue }) => {
    try {
      const response = await fetchPurchaseOrdersApi(params || {});
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch purchase orders');
    }
  }
);

export const fetchPurchaseOrderDetail = createAsyncThunk(
  'po/fetchDetail',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await fetchPurchaseOrderApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch purchase order');
    }
  }
);

export const fetchPoStats = createAsyncThunk(
  'po/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchPoStatsApi();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
    }
  }
);

export const fetchPoFiltersData = createAsyncThunk(
  'po/fetchFiltersData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchPoFiltersDataApi();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch filters data');
    }
  }
);

export const approvePurchaseOrder = createAsyncThunk(
  'po/approve',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await approvePoApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve');
    }
  }
);

export const rejectPurchaseOrder = createAsyncThunk(
  'po/reject',
  async ({ id, reason }: { id: number; reason: string }, { rejectWithValue }) => {
    try {
      const response = await rejectPoApi(id, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject');
    }
  }
);

export const submitPurchaseOrder = createAsyncThunk(
  'po/submit',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await submitPoApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit');
    }
  }
);

export const unsubmitPurchaseOrder = createAsyncThunk(
  'po/unsubmit',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await unsubmitPoApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to unsubmit');
    }
  }
);

export const rejectAtrPurchaseOrder = createAsyncThunk(
  'po/rejectAtr',
  async ({ id, comment }: { id: number; comment: string }, { rejectWithValue }) => {
    try {
      const response = await rejectAtrApi(id, comment);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject ATR');
    }
  }
);

export const cancelPurchaseOrder = createAsyncThunk(
  'po/cancel',
  async ({ id, reason }: { id: number; reason?: string }, { rejectWithValue }) => {
    try {
      const response = await cancelPoApi(id, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel');
    }
  }
);

export const deletePurchaseOrder = createAsyncThunk(
  'po/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await deletePoApi(id);
      return { id, ...response };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete');
    }
  }
);

export const fulfillPurchaseOrder = createAsyncThunk(
  'po/fulfill',
  async ({ id, fulfillmentId }: { id: number; fulfillmentId: number }, { rejectWithValue }) => {
    try {
      const response = await fulfillPoApi(id, fulfillmentId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update fulfillment');
    }
  }
);

export const splitPurchaseOrder = createAsyncThunk(
  'po/split',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await splitPoApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to split');
    }
  }
);

export const mergePurchaseOrders = createAsyncThunk(
  'po/merge',
  async ({ mainOrderId, orderIds }: { mainOrderId: number; orderIds: number[] }, { rejectWithValue }) => {
    try {
      const response = await mergePoApi(mainOrderId, orderIds);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to merge');
    }
  }
);

export const bulkApprovePurchaseOrders = createAsyncThunk(
  'po/bulkApprove',
  async (orderIds: number[], { rejectWithValue }) => {
    try {
      const response = await bulkApprovePoApi(orderIds);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk approve');
    }
  }
);

export const bulkRejectPurchaseOrders = createAsyncThunk(
  'po/bulkReject',
  async ({ orderIds, reason }: { orderIds: number[]; reason: string }, { rejectWithValue }) => {
    try {
      const response = await bulkRejectPoApi(orderIds, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk reject');
    }
  }
);

// ==================== Slice ====================

const poSlice = createSlice({
  name: 'purchaseOrders',
  initialState,
  reducers: {
    setPoFilters: (state, action: PayloadAction<Partial<PoListParams>>) => {
      state.filters = { ...state.filters, ...action.payload, page: 1 };
    },
    resetPoFilters: (state) => {
      state.filters = { per_page: 10, page: 1 };
    },
    setPoPage: (state, action: PayloadAction<number>) => {
      state.filters.page = action.payload;
    },
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    toggleSelectedId: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      if (state.selectedIds.includes(id)) {
        state.selectedIds = state.selectedIds.filter((sid) => sid !== id);
      } else {
        state.selectedIds = [...state.selectedIds, id];
      }
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    clearCurrentPo: (state) => {
      state.current = null;
    },
    clearPoError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch list
      .addCase(fetchPurchaseOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data;
        state.currentPage = action.payload.meta.current_page;
        state.lastPage = action.payload.meta.last_page;
        state.total = action.payload.meta.total;
        state.perPage = action.payload.meta.per_page;
        if (action.payload.status_counts) {
          state.statusCounts = action.payload.status_counts;
        }
      })
      .addCase(fetchPurchaseOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch detail
      .addCase(fetchPurchaseOrderDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrderDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.current = action.payload.data;
      })
      .addCase(fetchPurchaseOrderDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload as string;
      })
      // Fetch stats
      .addCase(fetchPoStats.fulfilled, (state, action) => {
        state.statusCounts = action.payload.data;
      })
      // Fetch filters data
      .addCase(fetchPoFiltersData.fulfilled, (state, action) => {
        state.filtersData = action.payload.data;
      })
      // Approve
      .addCase(approvePurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(approvePurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? action.payload.data : po
          );
        }
      })
      .addCase(approvePurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Reject
      .addCase(rejectPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(rejectPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? action.payload.data : po
          );
        }
      })
      .addCase(rejectPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Submit
      .addCase(submitPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(submitPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? action.payload.data : po
          );
        }
      })
      .addCase(submitPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Unsubmit
      .addCase(unsubmitPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(unsubmitPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? action.payload.data : po
          );
        }
      })
      .addCase(unsubmitPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Reject ATR
      .addCase(rejectAtrPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(rejectAtrPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.po) {
          state.current = action.payload.po;
        }
        if (state.list.length > 0) {
          state.list = state.list.map(po =>
            po.id === action.payload.po?.id ? { ...po, ...action.payload.po } : po
          );
        }
      })
      .addCase(rejectAtrPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Cancel
      .addCase(cancelPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(cancelPurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.data) {
          state.current = action.payload.data;
          state.list = state.list.map((po) =>
            po.id === action.payload.data.id ? action.payload.data : po
          );
        }
      })
      .addCase(cancelPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deletePurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deletePurchaseOrder.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.list = state.list.filter((po) => po.id !== action.payload.id);
        state.selectedIds = state.selectedIds.filter((id) => id !== action.payload.id);
      })
      .addCase(deletePurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Fulfill
      .addCase(fulfillPurchaseOrder.fulfilled, (state, action) => {
        if (action.payload.data) {
          state.current = action.payload.data;
        }
      })
      // Split
      .addCase(splitPurchaseOrder.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(splitPurchaseOrder.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(splitPurchaseOrder.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Merge
      .addCase(mergePurchaseOrders.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(mergePurchaseOrders.fulfilled, (state) => {
        state.actionLoading = false;
        state.selectedIds = [];
      })
      .addCase(mergePurchaseOrders.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Bulk approve
      .addCase(bulkApprovePurchaseOrders.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(bulkApprovePurchaseOrders.fulfilled, (state) => {
        state.actionLoading = false;
        state.selectedIds = [];
      })
      .addCase(bulkApprovePurchaseOrders.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      // Bulk reject
      .addCase(bulkRejectPurchaseOrders.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(bulkRejectPurchaseOrders.fulfilled, (state) => {
        state.actionLoading = false;
        state.selectedIds = [];
      })
      .addCase(bulkRejectPurchaseOrders.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPoFilters,
  resetPoFilters,
  setPoPage,
  setSelectedIds,
  toggleSelectedId,
  clearSelectedIds,
  clearCurrentPo,
  clearPoError,
} = poSlice.actions;

export default poSlice.reducer;

// Selectors
export const selectPoList = (state: RootState) => state.purchaseOrders.list;
export const selectPoCurrent = (state: RootState) => state.purchaseOrders.current;
export const selectPoLoading = (state: RootState) => state.purchaseOrders.loading;
export const selectPoDetailLoading = (state: RootState) => state.purchaseOrders.detailLoading;
export const selectPoActionLoading = (state: RootState) => state.purchaseOrders.actionLoading;
export const selectPoError = (state: RootState) => state.purchaseOrders.error;
export const selectPoStatusCounts = (state: RootState) => state.purchaseOrders.statusCounts;
export const selectPoFiltersData = (state: RootState) => state.purchaseOrders.filtersData;
export const selectPoFilters = (state: RootState) => state.purchaseOrders.filters;
export const selectPoPagination = (state: RootState) => ({
  currentPage: state.purchaseOrders.currentPage,
  lastPage: state.purchaseOrders.lastPage,
  total: state.purchaseOrders.total,
  perPage: state.purchaseOrders.perPage,
});
export const selectSelectedIds = (state: RootState) => state.purchaseOrders.selectedIds;
